import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSpinner, FaSearch, FaFilter, FaEye } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { getUserQuotations } from '../services/quotationAuthService';

const STATUS_COLORS = {
  pending: 'warning',
  estimado_listo: 'info',
  completada: 'success',
  rechazada: 'danger'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  estimado_listo: 'Estimado Listo',
  completada: 'Completada',
  rechazada: 'Rechazada'
};

export default function QuotationListPage() {
  const { user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const results = await getUserQuotations(user);
        setQuotations(results);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, [user]);

  const filteredQuotations = quotations
    .filter(q => filter === 'all' || q.get('status') === filter)
    .filter(q => {
      const searchLower = search.toLowerCase();
      return (
        q.get('userEmail').toLowerCase().includes(searchLower) ||
        q.get('magicLink').toLowerCase().includes(searchLower) ||
        q.get('items').some(item => 
          item.name.toLowerCase().includes(searchLower)
        )
      );
    });

  if (loading) {
    return (
      <div className="text-center py-5">
        <FaSpinner className="spin" size={32} />
        <p className="mt-3">Cargando sus cotizaciones...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Mis Cotizaciones</h2>
        <Link to="/quotation" className="btn btn-primary btn-sm">
          Nueva Cotización
        </Link>
      </div>

      {/* Filtros y búsqueda */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <FaSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por email, ID o producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <FaFilter />
            </span>
            <select
              className="form-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="pending">Pendientes</option>
              <option value="processing">En Proceso</option>
              <option value="completed">Completadas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de cotizaciones */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Productos</th>
              <th>Magic Link</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotations.map((quotation) => (
              <tr key={quotation.id}>
                <td>
                  {new Date(quotation.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <span className={`badge bg-${STATUS_COLORS[quotation.get('status') || 'pending']}`}>
                    {STATUS_LABELS[quotation.get('status') || 'pending']}
                  </span>
                </td>
                <td>
                  <small>
                    {quotation.get('items').map(item => item.name).join(', ')}
                  </small>
                </td>
                <td>
                  <code className="small">
                    {quotation.get('magicLink')}
                  </code>
                </td>
                <td>
                  <Link
                    to={`/dashboard/quotation/${quotation.get('magicLink')}`}
                    className="btn btn-sm btn-light"
                    title="Ver detalles"
                  >
                    <FaEye />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredQuotations.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted mb-0">
            {quotations.length === 0
              ? "Aún no tiene cotizaciones"
              : "No se encontraron cotizaciones que coincidan con los filtros"}
          </p>
        </div>
      )}
    </div>
  );
}
